/**
 * SURVEYノード（POPUP 全画面ゲート）送信後の「分岐（ルーティング）」解決ロジック（#265・ADR-0011）。
 *
 * SURVEYノードの回答内容に応じて次のノードを出し分ける。指定した単一選択設問（ルーティング設問）の
 * 選んだ選択肢に遷移先マップ（routing: optionId → nodeKey）を対応させ、未該当・未指定（線形）は
 * デフォルト遷移先（defaultTargetNodeKey）へ進む。遷移先マップは再利用テンプレ（Survey）ではなく
 * 配置（SURVEYノード）側の config に保持する（配置ごとに行き先が異なるため）。
 *
 * 副作用を持たず React state にも依存しない純粋関数として切り出し、レビュー・手動 /verify での
 * 分岐確認をしやすくしている。遷移先ノードの実在チェックはプレイヤー側の責務（ここでは行わない）。
 */

import type { SurveyNodeConfig } from '@/types/interactiveVideo'

/** ルーティング解決に必要な1設問ぶんの回答（選んだ選択肢のみ見る）。 */
export interface RoutingAnswer {
  questionId: string
  optionIds: string[]
}

/** ルーティング解決に必要な config 部分（SurveyNodeConfig の一部）。 */
export type SurveyRoutingConfig = Pick<
  SurveyNodeConfig,
  'routingQuestionId' | 'routing' | 'defaultTargetNodeKey'
>

/**
 * SURVEYノード送信後の遷移先 nodeKey を、ルーティング設問の回答から解決する。
 *
 * - ルーティング設問（routingQuestionId）が指定され、その回答で選んだ選択肢が遷移先マップ（routing）に
 *   あれば、その nodeKey を返す。
 * - ルーティング未指定（線形）・回答なし・マップに未該当のときはデフォルト遷移先（defaultTargetNodeKey）。
 * - デフォルトも無ければ null（フロー終端）。
 *
 * ルーティング設問は単一選択（SINGLE）前提のため、選択肢は先頭（optionIds[0]）のみ参照する。
 *
 * `nodeExists` を渡すと遷移先ノードの実在を検証し、割り当て済みでも遷移先が消えている場合は「未該当」
 * としてデフォルトへ落とす（フロー終端を避ける・#265）。未指定なら config だけで解決する（実在検証なし）。
 */
export function resolveSurveyTargetNodeKey(
  config: SurveyRoutingConfig,
  answers: ReadonlyArray<RoutingAnswer>,
  nodeExists?: (nodeKey: string) => boolean,
): string | null {
  const { routingQuestionId, routing, defaultTargetNodeKey } = config
  // 実在する遷移先だけ採用する（nodeExists 未指定なら実在チェックを省く）。
  const usable = (nodeKey: string | null | undefined): nodeKey is string =>
    !!nodeKey && (!nodeExists || nodeExists(nodeKey))
  const fallback = usable(defaultTargetNodeKey) ? defaultTargetNodeKey : null

  // ルーティング設問が未指定＝線形ゲート（デフォルトのみ）の退化ケース。
  if (!routingQuestionId || !routing) return fallback

  const routingAnswer = answers.find((answer) => answer.questionId === routingQuestionId)
  const selectedOptionId = routingAnswer?.optionIds[0]
  if (!selectedOptionId) return fallback

  // 選んだ選択肢に実在する遷移先が割り当てられていればそこへ、無ければデフォルトへ。
  const target = routing[selectedOptionId]
  return usable(target) ? target : fallback
}
